/**
 * Normalise a product image-gallery value into a real `string[]`.
 *
 * The API's `imageUrls` can arrive in more than one shape depending on how the
 * row was stored over the app's history:
 *   • a real array                ["/v1/files/a.png", "/v1/files/b.png"]
 *   • a JSON-encoded array string "[\"/v1/files/a.png\"]"        ← double-encoded
 *   • a doubly-encoded string     "\"[\\\"/v1/files/a.png\\\"]\""
 *   • a single bare URL string    "/v1/files/a.png"
 *   • null / undefined / ""       → []
 *
 * Always returns a plain array (never null) so callers can safely `.map`,
 * `.length`, `[0]`, `.forEach` without re-checking the shape. Blanks are dropped.
 */
export function toImageUrls(value: unknown): string[] {
  return collectUrls(value);
}

function collectUrls(value: unknown, depth = 0): string[] {
  if (value == null || depth > 6) return [];
  if (Array.isArray(value)) return value.flatMap((v) => collectUrls(v, depth + 1));
  if (typeof value !== 'string') return [];

  const s = value.trim();
  if (!s) return [];

  // Only try to JSON-decode strings that look encoded; a bare URL ("/v1/files/…")
  // is taken verbatim.
  if (s[0] === '[' || s[0] === '"') {
    try {
      const parsed = JSON.parse(s);
      if (parsed !== s) return collectUrls(parsed, depth + 1);
    } catch {
      /* not valid JSON — treat as a plain URL */
    }
  }
  return [s];
}
